import React, { useEffect } from "react";

import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Route, BrowserRouter, Routes as SharedRoutes, useLocation } from "react-router-dom";

import AuthenticatedRegisteredRoutes from "./AuthenticatedRegisteredRoutes";
import AuthenticatedUnregisteredRoutes from "./AuthenticatedUnregisteredRoutes";
import GtmEvents from "./GtmEvents";
import ScrollRestoration from "./ScrollRestoration";
import UnauthenticatedRoutes from "./UnauthenticatedRoutes";
import SiteBanner from "../components/Banner/SiteBanner";
import LoadingDialog from "../components/Common/Dialogs/LoadingDialog";
import AccountInactive from "../pages/AccountInactive/AccountInactive";
import Unavailable from "../pages/AppStatus/Unavailable";
import RollOverRedirect from "../pages/Redirect/RollOver";
import { Status } from "../store/parentApi";
import { RootState } from "../store/reducers";
import axiosClient, { app_uri } from "../utils/axiosFactory";

const RouteContent = () => {
  const location = useLocation();
  const RETURN_URL_KEY = "returnUrl";
  const appStatusIsLoaded = useSelector((state: RootState) => state.appStatus.isLoaded);
  const appStatus = useSelector((state: RootState) => state.appStatus.appStatus);
  const appUnavailable = appStatusIsLoaded && appStatus !== null && appStatus.status === Status.UnAvailable;
  const auth = useSelector((state: RootState) => state.auth);
  const { registeredUser, user, hasCheckedUser } = auth;

  useEffect(() => {
    if (hasCheckedUser && !user && !sessionStorage.getItem(RETURN_URL_KEY)) {
      const defaultReturnUrl = ["/", "/silent-renew-oidc", "/signin-oidc", "/account-inactive"];
      const returnUrl = defaultReturnUrl.includes(location.pathname) ? "/" : location.pathname + location.search;

      sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    }
  }, [user, hasCheckedUser, location]);

  // Bootstrap check: only runs when the user is authenticated and registered.
  // Mirrors BootstrapBloc in the native app — account authorisation is a routing concern,
  // not an authentication concern, so it lives here rather than in SignInOidc.
  const bootstrapQuery = useQuery(
    ["bootstrap", user?.access_token],
    async () => {
      const response = await axiosClient().get<{ accountStatus: string }>(`${app_uri}/api/v3/bootstrap`);
      return response.data;
    },
    {
      enabled: !!(user && registeredUser),
      staleTime: 5 * 60 * 1000,
    }
  );

  // Fail-closed only for explicitly non-active statuses (Inactive, Deleted, NoMatchingContact).
  // Unknown (DB unreachable) and network errors → fail-open, mirroring the native app's
  // BootstrapBloc and the API contract comment ("caller decides fail-open vs fail-closed").
  // An inactive user who slips through while the DB is down still can't do anything useful
  // because every individual endpoint also guards via RequireActiveCustomer.
  const isAccountInactive =
    !!(user && registeredUser) &&
    !bootstrapQuery.isLoading &&
    !bootstrapQuery.isError &&
    bootstrapQuery.data?.accountStatus !== "Active" &&
    bootstrapQuery.data?.accountStatus !== "Unknown";

  return (
    <>
      <SiteBanner />
      <ScrollRestoration />
      <SharedRoutes>
        <Route path="/rollover-redirect" element={<RollOverRedirect />}></Route>
      </SharedRoutes>
      {appUnavailable ? (
        <Unavailable />
      ) : !hasCheckedUser ? (
        <LoadingDialog open={true} />
      ) : isAccountInactive ? (
        <AccountInactive />
      ) : user && registeredUser ? (
        bootstrapQuery.isLoading ? (
          <LoadingDialog open={true} />
        ) : (
          <>
            <GtmEvents />
            <AuthenticatedRegisteredRoutes />
          </>
        )
      ) : user && !registeredUser ? (
        <AuthenticatedUnregisteredRoutes />
      ) : (
        <UnauthenticatedRoutes />
      )}
    </>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <RouteContent />
    </BrowserRouter>
  );
};

export default Routes;
